import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { VENUE_META, STATUS_META, shortHash, timeAgo, logoUrl } from "@/lib/meta";
import { Rocket, Flame, TrendingUp, Wallet, AlertCircle, Sparkles, ArrowUpRight, Activity } from "lucide-react";

const StatCard = ({ label, value, sub, accent, testid }) => (
  <div className="rounded-2xl border border-line bg-surface p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-dust">{label}</p>
    <p className="mt-2 font-mono text-2xl font-semibold" style={{ color: accent || "#FAF6EE" }}>{value}</p>
    {sub && <p className="mt-1 text-xs text-dust">{sub}</p>}
  </div>
);

const levelColor = { juice: "#FFC83B", pulp: "#10E396", ai: "#FF9D00" };

export default function Overview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-dust">Loading mission control…</div>;

  const empty = data.launches.length === 0;
  const s = data.stats;

  if (empty) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-5 py-24 text-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-raised text-juice"><Rocket size={28} /></div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Launch it. Wen Moon runs the engine.</h1>
        <p className="mt-4 max-w-md text-dust">Pick a pad, connect socials, set a budget. Pay upfront. Watch it fly.</p>
        <Button onClick={() => navigate("/app/launch")} size="lg" className="mt-8 gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="empty-start-launch">
          <Rocket size={18} /> Start a launch
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
          <p className="text-sm text-dust">What the agents are doing.</p>
        </div>
        <Button onClick={() => navigate("/app/launch")} className="gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="overview-new-launch">
          <Rocket size={16} /> New launch
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard testid="stat-active" label="Active launches" value={s.active_launches} accent="#FFC83B" />
        <StatCard testid="stat-balance" label="Fuel balance" value={`$${s.juice_balance}`} accent="#10E396" />
        <StatCard testid="stat-fees" label="Fuel bought" value={`$${s.fees_paid}`} sub="Prepaid juice (seed + boosts)" />
        <StatCard testid="stat-burned" label="$MOON burned" value={`$${s.juice_burned.toFixed(2)}`} sub="buy+burn (protocol + expired)" accent="#10E396" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* left: needs you + launches */}
        <div className="space-y-6 lg:col-span-2">
          {data.needs_you.length > 0 && (
            <div className="rounded-2xl border border-warn/30 bg-warn/5 p-5" data-testid="needs-you">
              <div className="mb-3 flex items-center gap-2 text-warn"><AlertCircle size={16} /><h2 className="text-sm font-semibold uppercase tracking-wider">Needs you</h2></div>
              <div className="space-y-2">
                {data.needs_you.map((n, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-surface px-4 py-2.5 text-sm">
                    <span className="text-cream">{n.message}</span>
                    {n.launch_id && <Link to={`/app/launches/${n.launch_id}`} className="text-juice hover:underline">Open</Link>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-line bg-surface" data-testid="launches-table">
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <h2 className="text-sm font-semibold">Launches</h2>
              <Link to="/app/launches" className="text-xs text-dust hover:text-cream">View all</Link>
            </div>
            {data.launches.map((l) => {
              const v = VENUE_META[l.venue] || {};
              const st = STATUS_META[l.status] || STATUS_META.draft;
              return (
                <Link key={l.id} to={`/app/launches/${l.id}`} className="grid grid-cols-[1.4fr_1fr_1fr_0.8fr] items-center gap-2 border-b border-line/60 px-5 py-3.5 transition-colors hover:bg-raised/40" data-testid={`launch-row-${l.ticker}`}>
                  <div className="flex items-center gap-2.5">
                    {logoUrl(l.logo) && <img src={logoUrl(l.logo)} alt="" className="h-7 w-7 rounded-md border border-line object-cover" />}
                    <div>
                      <span className="font-mono text-sm text-cream">${l.ticker}</span>
                      <p className="text-xs text-dust">{l.name}</p>
                    </div>
                  </div>
                  <span className="text-sm text-dust">{v.name}</span>
                  <span className={`w-fit rounded-full border px-2.5 py-0.5 text-xs ${st.cls}`}>{st.label}</span>
                  <span className="text-right font-mono text-xs text-dust">${l.budget_spent}/${l.budget_total}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* right: live feed + burns */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-line bg-surface p-5" data-testid="live-feed">
            <div className="mb-3 flex items-center gap-2"><Activity size={15} className="text-ai" /><h2 className="text-sm font-semibold">Live feed</h2></div>
            <div className="space-y-3">
              {data.live_feed.map((e) => (
                <div key={e.id} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: levelColor[e.level] || "#A19A8A" }} />
                  <div>
                    <p className="text-sm leading-snug text-cream">{e.message}</p>
                    <p className="font-mono text-[11px] text-dust">{timeAgo(e.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-surface p-5" data-testid="overview-burns">
            <div className="mb-3 flex items-center gap-2"><Flame size={15} className="text-pulp" /><h2 className="text-sm font-semibold">Recent pulp</h2></div>
            <div className="space-y-2">
              {data.burns.map((b) => (
                <div key={b.id} className="flex items-center justify-between font-mono text-xs">
                  <span className={b.kind === "juice" ? "text-juice" : "text-pulp"}>${b.token}</span>
                  <span className="text-cream">${b.amount_usd.toFixed(2)}</span>
                  <span className="text-dust">{timeAgo(b.created_at)}</span>
                </div>
              ))}
            </div>
            <Link to="/app/burns" className="mt-4 flex items-center gap-1 text-xs text-juice hover:underline">Full ledger <ArrowUpRight size={12} /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
